/**
 * Content Security Policy Utilities
 * Provides helper functions for managing CSP in the browser
 */

/**
 * CSP Utility Class
 */
class CSPUtils {
  constructor() {
    this.nonce = null;
    this.supportsEventListeners = typeof window !== 'undefined' && window.addEventListener;
    this.supportsFetch = typeof fetch !== 'undefined';
    this.isOldBrowser = this.detectOldBrowser();
  }

  /**
   * Detect if browser has limited CSP support
   */
  detectOldBrowser() {
    if (typeof navigator === 'undefined') return false;

    const userAgent = navigator.userAgent;

    // Check for IE 10 and below
    if (/MSIE\s+([0-9]+)/.test(userAgent)) return true;

    // Check for IE 11
    if (/Trident.*rv:([0-9]+)/.test(userAgent)) return true;

    // Check for legacy Edge (not Chromium-based)
    if (/Edge\/([0-9]+)/.test(userAgent) && !/Edg\//.test(userAgent)) return true;

    // Check for old Firefox (< 45)
    const firefoxMatch = userAgent.match(/Firefox\/([0-9]+)/);
    if (firefoxMatch && parseInt(firefoxMatch[1]) < 45) return true;

    // Check for old Chrome (< 40)
    const chromeMatch = userAgent.match(/Chrome\/([0-9]+)/);
    if (chromeMatch && parseInt(chromeMatch[1]) < 40) return true;

    return false;
  }

  /**
   * Get the current CSP nonce
   */
  getNonce() {
    if (this.nonce) return this.nonce;
    
    // Try to get nonce from global variable
    if (typeof window !== 'undefined' && window.__CSP_NONCE__) {
      this.nonce = window.__CSP_NONCE__;
      return this.nonce;
    }
    
    // Try to get nonce from meta tag
    if (typeof document !== 'undefined') {
      const metaTag = document.querySelector('meta[name="csp-nonce"]');
      if (metaTag) {
        this.nonce = metaTag.getAttribute('content');
        return this.nonce;
      }
    }
    
    return null;
  }

  /**
   * Set nonce on all script tags that don't have one
   */
  setNonceOnScripts() {
    const nonce = this.getNonce();
    if (!nonce) return;

    const scripts = document.querySelectorAll('script:not([nonce])');
    scripts.forEach(script => {
      script.setAttribute('nonce', nonce);
    });
  }

  /**
   * Set nonce on all style tags that don't have one
   */
  setNonceOnStyles() {
    const nonce = this.getNonce();
    if (!nonce) return;

    const styles = document.querySelectorAll('style:not([nonce]), link[rel="stylesheet"]:not([nonce])');
    styles.forEach(style => {
      style.setAttribute('nonce', nonce);
    });
  }

  /**
   * Create a script element with proper nonce
   */
  createScript(content, src = null, async = false) {
    const script = document.createElement('script');
    const nonce = this.getNonce();
    
    if (nonce) {
      script.setAttribute('nonce', nonce);
    }
    
    if (src) {
      script.src = src;
    } else {
      script.textContent = content;
    }
    
    if (async) {
      script.async = true;
    }
    
    return script;
  }

  /**
   * Create a style element with proper nonce
   */
  createStyle(content, href = null) {
    if (href) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      const nonce = this.getNonce();
      if (nonce) {
        link.setAttribute('nonce', nonce);
      }
      return link;
    } else {
      const style = document.createElement('style');
      style.textContent = content;
      const nonce = this.getNonce();
      if (nonce) {
        style.setAttribute('nonce', nonce);
      }
      return style;
    }
  }

  // safeExecuteScript() was removed 2026-08-09.
  //
  // It built a `new Function(...)` from a code string and fell back to `eval()`,
  // and it was the ONLY construct in the entire client bundle that required
  // 'unsafe-eval' in the Content-Security-Policy — inside the module named
  // csp-utils. Nothing ever called it.
  //
  // With it gone, 'unsafe-eval' is gone from script-src. Do not reintroduce a
  // string-to-code helper here; it costs the whole application its eval
  // protection to serve one call site, and there were zero.

  /**
   * Report CSP violation
   */
  async reportViolation(violation) {
    if (!this.supportsFetch) {
      console.warn('[CSP] Fetch not supported, cannot report violation');
      return;
    }

    try {
      await fetch('/api/csp-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...violation,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent
        })
      });
    } catch (error) {
      console.warn('[CSP] Failed to report violation:', error);
    }
  }

  /**
   * Set up CSP violation monitoring
   */
  setupViolationMonitoring() {
    if (!this.supportsEventListeners) {
      console.warn('[CSP] Event listeners not supported, cannot monitor violations');
      return;
    }

    const handleViolation = (event) => {
      console.warn('[CSP] Violation detected:', event);
      this.reportViolation({
        blockedURI: event.blockedURI,
        documentURI: event.documentURI,
        effectiveDirective: event.effectiveDirective,
        originalPolicy: event.originalPolicy,
        referrer: event.referrer,
        sample: event.sample,
        sourceFile: event.sourceFile,
        lineNumber: event.lineNumber,
        columnNumber: event.columnNumber
      });
    };

    // Modern browsers
    window.addEventListener('securitypolicyviolation', handleViolation);

    // Fallback for older browsers
    if (this.isOldBrowser) {
      // Set up error listener as fallback
      window.addEventListener('error', (event) => {
        if (event.message && event.message.includes('Content Security Policy')) {
          handleViolation({
            blockedURI: event.filename || 'unknown',
            documentURI: window.location.href,
            effectiveDirective: 'script-src',
            originalPolicy: 'unknown',
            referrer: document.referrer,
            sample: event.message,
            sourceFile: event.filename,
            lineNumber: event.lineno,
            columnNumber: event.colno
          });
        }
      });
    }
  }

  /**
   * Check if CSP is supported and active
   */
  checkCSPSupport() {
    if (typeof document === 'undefined') return false;

    // Check for meta CSP tag
    const metaCSP = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    if (metaCSP) return true;

    // Check for CSP header (we can't directly access headers, but we can test)
    try {
      // Create a test script that should be blocked by CSP
      const testScript = this.createScript('window.__CSP_TEST__ = true;');
      const head = document.head;
      head.appendChild(testScript);

      // Capture references before setTimeout to avoid issues with DOM cleanup
      setTimeout(() => {
        try {
          if (head && testScript && testScript.parentNode === head) {
            head.removeChild(testScript);
          }
        } catch (e) {
          // Ignore cleanup errors (DOM may have been cleaned up in tests)
        }
      }, 100);

      // If the script executed, CSP might not be active or is too permissive
      return !window.__CSP_TEST__;
    } catch (error) {
      // If we can't even create the script, CSP is likely active
      return true;
    }
  }

  /**
   * Initialize CSP utilities
   */
  initialize() {
    // Set up nonce handling
    this.setNonceOnScripts();
    this.setNonceOnStyles();

    // Set up violation monitoring
    this.setupViolationMonitoring();

    // Log CSP status
    const isSupported = this.checkCSPSupport();
    console.info(`[CSP] Support detected: ${isSupported ? 'Yes' : 'No'}`);
    
    if (this.isOldBrowser) {
      console.warn('[CSP] Old browser detected, using fallback mechanisms');
    }

    // Set up mutation observer for dynamic content
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Handle new scripts and styles
              if (node.tagName === 'SCRIPT' && !node.hasAttribute('nonce')) {
                this.setNonceOnScripts();
              }
              if ((node.tagName === 'STYLE' || node.tagName === 'LINK') && !node.hasAttribute('nonce')) {
                this.setNonceOnStyles();
              }
            }
          });
        });
      });

      observer.observe(document.head || document.documentElement, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Get CSP diagnostic information
   */
  getDiagnostics() {
    const nonce = this.getNonce();
    const isSupported = this.checkCSPSupport();
    
    return {
      nonce: nonce ? `${nonce.substring(0, 8)}...` : 'Not found',
      supported: isSupported,
      oldBrowser: this.isOldBrowser,
      supportsEventListeners: this.supportsEventListeners,
      supportsFetch: this.supportsFetch,
      metaCSP: !!document.querySelector('meta[http-equiv="Content-Security-Policy"]'),
      scriptsWithNonce: document.querySelectorAll('script[nonce]').length,
      scriptsWithoutNonce: document.querySelectorAll('script:not([nonce])').length,
      stylesWithNonce: document.querySelectorAll('style[nonce], link[nonce]').length,
      stylesWithoutNonce: document.querySelectorAll('style:not([nonce]), link:not([nonce])').length
    };
  }
}

// Create singleton instance
const cspUtils = new CSPUtils();

// Auto-initialize when DOM is ready
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => cspUtils.initialize());
  } else {
    cspUtils.initialize();
  }
}

// Export for use in other modules
export default cspUtils;
export { CSPUtils };

// Also provide global access for debugging
if (typeof window !== 'undefined') {
  window.__CSP_UTILS__ = cspUtils;
}